import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Alert, Button, Col, Modal, Nav, Row, Tab } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { api_without_cancellation } from "../../api/api";
import { getCaseId, getClientId, formatCalendarDateForInput } from "../../Utils/helper";
import SelectStateModal from "../TreatmentPage/modals/state-modal/SelectStateModal";
import ContactPanelForEditPopups from "../TreatmentPage/components/ContactPanelForEditPopups";

import { useCostManagement } from "./CostManagementContext";
import { useUpdateTrigger } from "./TriggerUpdateContext";

// ─── Helpers ────────────────────────────────────────────────────────────────

// Date inputs preserve calendar days without converting through local time.
function convertToHtmlDateInputValue(isoDateString) {
  return formatCalendarDateForInput(isoDateString);
}

// Page IDs that carry an entity contact we want to auto-fill payee fields from
// const ENTITY_COST_CATEGORY_PAGE_IDS = new Set([8, 14]);

// Treat null / undefined / "" as empty
// const hasValue = (value) => value !== null && value !== undefined && value !== "";

// Different API shapes use different key names — normalise them all to a number
const getCategoryPageId = (page) =>
  Number(page?.page_id ?? page?.pageId ?? page?.pageID ?? page?.page?.id ?? page?.page ?? page?.id);

// Pull the entity object out of whatever shape the page record is in
const getCategoryEntity = (page) => page?.entity ?? page?.cost_entity ?? page?.data ?? page;

// Walk the entity tree to find the contact object
// const getEntityContact = (entity) =>
//   entity?.payee_contact ??
//   entity?.contact ??
//   entity?.provider?.payee_contact ??
//   entity?.provider?.contact;

// Build a display name from the contact or fall back to the entity name
// const getContactName = (contact, entity) => {
//   if (typeof contact === "string") return contact;

//   const fullName = [contact?.first_name, contact?.middle_name, contact?.last_name]
//     .filter(Boolean)
//     .join(" ");

//   return contact?.name || fullName || entity?.provider?.name || entity?.name;
// };

// Show "Page Name - Cost Name" when a cost_name exists, otherwise just the page name
const getCategoryLabel = (page) => {
  return page?.name;
};

// ─── Card helpers ────────────────────────────────────────────────────────────

// Different card shapes — grab whichever id field exists
const getCardValue = (card) =>
  card?.id ?? card?.card_id ?? card?.last_four ?? card?.last4 ?? card?.card_number ?? "";

// Build "Bank Name ***1234" label, or just the name if no last-four available
const getCardLabel = (card) => {
  const name = card?.name || card?.card_name || card?.nickname || card?.bank_name || "Credit Card";
  const lastFour = card?.last_four || card?.last4 || card?.last_4 || card?.card_number;
  return lastFour ? `${name} ***${String(lastFour).slice(-4)}` : name;
};

// ─── Simple debounce (no lodash dep needed here) ─────────────────────────────
const debounce = (func, delay) => {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };
};

// ─── Component ───────────────────────────────────────────────────────────────

const CostManagementModal = () => {
  const { isVisible, isEdit, cost, hideModal: onHide } = useCostManagement();

  const currentCase = useSelector((state) => state?.caseData?.current);
  const client = useSelector((state) => state?.client?.current);

  const caseId = getCaseId() ?? 0;
  const clientId = getClientId() ?? 0;

  // Pre-format the date once; the memo below depends on it
  const date = cost?.due_date ? convertToHtmlDateInputValue(cost.due_date) : "";
  // Default values for RHF — rebuilds whenever the cost record changes
  const defaultValues = useMemo(
    () => ({
      date: "",
      payee: "",
      invoice_number: "",
      amount: 0,
      paid_by: "Pending",
      credit_card: "",
      check_number: "",
      memo: "",
      category: 8,
      note: "",
      source_key: "",
      source_model: "",
      source_id: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors: formErrors },
  } = useForm({ defaultValues, mode: "onTouched" });

  const { toggleTriggerUpdate } = useUpdateTrigger();

  // ── Local state ────────────────────────────────────────────────────────────
  const [submitError, setSubmitError] = useState(false);
  const [firmCards, setFirmCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [pages, setPages] = useState([]);
  const [showPageDropdown, setShowPageDropdown] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);

  // Search bar state (API call is stubbed out — wired up when endpoint is ready)
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false); // eslint-disable-line
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [statesAbrs, setStatesAbrs] = useState([]);

  // Ref so clicks outside the search dropdown close it
  const searchDropdownRef = useRef(null);

  // ── Register fields that RHF doesn't bind via JSX ─────────────────────────
  register("state");
  register("category", { required: "Required" });
  register("cost_name");
  register("source_key");
  register("source_model");
  register("source_id");
  // register("address1");
  // register("address2");
  // register("city");
  // register("state");
  // register("zip");

  const category = cost?.category ? cost?.category : watch("category");

  // Watch values needed for conditional rendering and auto-switching radio
  const payeeState = watch("state");
  const paidBy = watch("paid_by");

  // ── Effects ────────────────────────────────────────────────────────────────

  // Load US states once on mount (used by the state picker modal)
  useEffect(() => {
    const fetchStatesData = async () => {
      try {
        const response = await api_without_cancellation.get("/api/states/");
        if (response.status === 200) setStatesAbrs(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchStatesData();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    fetchPages();

    const fetchFirmCards = async () => {
      setCardsLoading(true);
      try {
        const response = await api_without_cancellation.get("/api/settlement-page/get-firm-cards/");
        const cards = Array.isArray(response.data)
          ? response.data
          : response.data?.data || response.data?.cards || [];
        setFirmCards(cards);
      } catch (error) {
        console.error(error);
        setFirmCards([]);
      } finally {
        setCardsLoading(false);
      }
    };

    fetchFirmCards();
  }, [isEdit, isVisible]);

  // intentionally omit defaultValues — we want this to run on open/close only

  // When adding (not editing) load categories and firm cards

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
        setIsFiltersOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const categoryRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setShowPageDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchPages = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/get-cost-categories/?case_id=${caseId}&client_id=${clientId}`
      );
      setPages(response.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!isVisible) return;
    setSubmitError(false);
    setSelectedPage(null); // clear previously picked category
    setShowPageDropdown(false);
    // reset() covers registered RHF fields; the extra fields set via setValue
    // (address1, address2, city, state, zip) are also registered via JSX, so
    // passing them explicitly in defaultValues keeps everything clean.
    if (!isEdit) {
      const selc = pages?.find((p) => p?.id === category);
      setSelectedPage(selc ?? null);
    }
    if (isEdit) {
      setValue("date", date);
      setValue("payee", cost?.payee ?? "");
      setValue("invoice_number", cost?.invoice_number ?? "");
      setValue("amount", cost?.amount ?? 0);
      setValue("paid_by", cost?.paid_by ?? "Credit Card");
      setValue("credit_card", cost?.credit_card ?? "");
      setValue("check_number", cost?.check_number ?? cost?.checkID?.cheque_number ?? "");
      setValue("memo", cost?.memo ?? "");
      setValue("category", cost?.category ?? null);
      setValue("note", cost?.note ?? "");
      setValue("source_key", cost?.source_key ?? "");
      setValue("source_model", cost?.source_model ?? "");
      setValue("source_id", cost?.source_object_id ?? null);
      setValue("address1", cost?.payee_contact?.address1 ?? "");
      setValue("address2", cost?.payee_contact?.address2 ?? "");
      setValue("city", cost?.payee_contact?.city ?? "");
      setValue("state", cost?.payee_contact?.state ?? "");
      setValue("zip", cost?.payee_contact?.zip ?? "");
      const selc = pages?.find((p) => p?.id === cost?.category);
      setSelectedPage(selc ?? null);
    }
  }, [isVisible, pages]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async () => {
    try {
      await api_without_cancellation.delete(`api/delete-cost/${cost?.id}/`);
      setSubmitError(false);
      setSelectedPage(null); // clear previously picked category
      setShowPageDropdown(false);
      // reset() covers registered RHF fields; the extra fields set via setValue
      // (address1, address2, city, state, zip) are also registered via JSX, so
      // passing them explicitly in defaultValues keeps everything clean.

      reset({
        ...defaultValues,
      });
      toggleTriggerUpdate();
      onHide();
    } catch (error) {
      console.error(error);
    }
  }, [cost?.id, onHide, toggleTriggerUpdate]);

  // Cancel = reset form + close (same full reset as open so nothing lingers)
  const handleCancel = () => {
    setSelectedPage(null);
    reset({
      ...defaultValues,
      address1: "",
      address2: "",
      city: "",
      state: "",
      zip: "",
    });
    onHide();
  };

  const onSubmit = async (values) => {
    // Strip the irrelevant payment field before sending
    const payload = {
      ...values,
      amount: parseFloat(values.amount),
      credit_card: values.paid_by === "Credit Card" ? values.credit_card : "",
      check_number: values.paid_by === "Check" ? values.check_number : "",
    };

    const url = `/api/add-cost/${clientId}/${caseId}/`;

    try {
      if (isEdit) {
        const editPayload = {
          ...payload,
          id: cost?.id,
        };
        await api_without_cancellation.patch(url, editPayload);
      } else {
        await api_without_cancellation.post(url, payload);
      }

      toggleTriggerUpdate();
      reset(defaultValues);
      onHide();
    } catch (error) {
      console.error("Error saving cost:", error.response ? error.response.data : error.message);
      setSubmitError(true);
    }
  };

  // Auto-fill payee + address fields from the selected category entity
  // const setEntityCostFields = (page) => {
  //   const entity = getCategoryEntity(page);

  //   setValue("cost_name", entity?.cost_name ?? "");
  //   setValue("source_key", entity?.source_key ?? page?.source_key ?? "");
  //   setValue("source_model", entity?.source_model ?? "");
  //   setValue("source_id", entity?.source_id ?? "");

  //   // Only pages in the set carry a contact we should pre-fill from
  //   if (!ENTITY_COST_CATEGORY_PAGE_IDS.has(getCategoryPageId(page))) return;

  //   const contact = getEntityContact(entity);
  //   const payeeName = getContactName(contact, entity);

  //   if (hasValue(payeeName))
  //     setValue("payee", payeeName, { shouldValidate: true, shouldDirty: true });
  //   if (hasValue(contact?.address1)) setValue("address1", contact.address1);
  //   if (hasValue(contact?.address2)) setValue("address2", contact.address2);
  //   if (hasValue(contact?.city)) setValue("city", contact.city);
  //   if (hasValue(contact?.state)) setValue("state", contact.state);
  //   if (hasValue(contact?.zip ?? contact?.zip_code))
  //     setValue("zip", contact.zip ?? contact.zip_code);
  //   if (hasValue(entity?.amount))
  //     setValue("amount", String(entity.amount), { shouldValidate: true, shouldDirty: true });
  // };

  const handlePageSelect = (page) => {
    setSelectedPage(page);
    setValue("category", page?.id, { shouldValidate: true, shouldDirty: true });
    // setEntityCostFields(page);
    setShowPageDropdown(false);
  };

  // Payee search — API call commented out until endpoint is finalised
  const handleSearch = async (value) => {
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }
    // TODO: uncomment when search endpoint is ready
    setIsSearching(true);
    try {
      const response = await api_without_cancellation.get("/api/filter-cost-payee/", {
        params: { query: value, client_id: getClientId(), case_id: getCaseId() },
      });
      if (response.status === 200) setSearchResults(response.data.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultSelect = (result) => {
    setValue("source_id", result?.source_id);
    setValue("source_model", result?.source_model);
    setValue("source_key", result?.source_key);
    setValue("payee", result?.entity_name);
    setValue("address1", result?.payee_contact?.address1);
    setValue("address2", result?.payee_contact?.address2);
    setValue("city", result?.payee_contact?.city);
    setValue("state", result?.payee_contact?.state);
    setValue("zip", result?.payee_contact?.zip);
    setValue("category", result?.page?.id);
    setValue("amount", result?.amount);
    setSelectedPage(result?.page);
    const selc = pages?.find((p) => p?.id === result?.page?.id);
    setSelectedPage(selc ?? null);
    setSearchResults([]);
    setSearchTerm("");
    setIsFiltersOpen(false);
  };

  // Debounce prevents a search request on every single keystroke
  const debouncedSearch = useCallback(debounce(handleSearch, 300), []);
  const [activeTab, setActiveTab] = useState("payee");
  // ── Render ─────────────────────────────────────────────────────────────────
  const [amountFocused, setAmountFocused] = useState(false);
  const [previousAmount, setPreviousAmount] = useState("");
  return (
    <>
      <Modal
        show={isVisible}
        onHide={handleCancel}
        dialogClassName="modal-dialog-centered modal-800p"
      >
        {/* Header */}
        <div
          className="text-center height-25 p-0 bg-primary modal-header"
          style={{ border: "none" }}
        >
          <div
            className="mx-auto height-25 text-white d-flex align-items-center"
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            {isEdit
              ? `Edit Cost for ${client?.first_name} ${client?.last_name} * ${currentCase?.case_type?.name}`
              : "Add Cost"}
          </div>
        </div>

        <Modal.Body style={{ padding: "0px" }}>
          {/* Server error banner */}
          {submitError && (
            <Alert
              className="mb-0 p-0 p-l-5"
              style={{
                border: "0px",
                borderRadius: "0px",
                textTransform: "uppercase",
                fontWeight: "600",
              }}
              variant="danger"
            >
              Error creating a cost record.
            </Alert>
          )}

          <form
            onSubmit={handleSubmit(onSubmit, (errors) => {
              // RHF calls this when validation fails before the API even fires
              const hasPayeeError = !!errors.payee;
              const hasPaymentError = !!(
                errors.amount ||
                // errors.invoice_number ||
                errors.date ||
                errors.category
              );

              if (hasPayeeError && !hasPaymentError) {
                setActiveTab("payee");
              } else if (!hasPayeeError && hasPaymentError) {
                setActiveTab("payment");
              }
              // if both tabs have errors, leave the user where they are
            })}
          >
            <Tab.Container defaultActiveKey={"payee"} activeKey={activeTab} onSelect={setActiveTab}>
              {/* Tab navigation */}
              <Nav
                variant="tabs"
                className="justify-content-around"
                style={{ marginTop: "5px", marginBottom: "5px" }}
              >
                <Nav.Link
                  className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-before"
                  eventKey="payee"
                >
                  Payee
                </Nav.Link>
                <Nav.Link
                  className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-after"
                  eventKey="payment"
                >
                  Payment
                </Nav.Link>
              </Nav>

              <div style={{ minHeight: "355px" }}>
                <Tab.Content>
                  {/* ── Payee tab ─────────────────────────────────────────── */}
                  <Tab.Pane style={{ minHeight: "355px" }} eventKey="payee">
                    {/* Payee search (stub — wired to debounced handler) */}
                    <div className="d-flex p-l-5">
                      <div style={{ width: "fit-content", maxWidth: "265px" }}>
                        <ContactPanelForEditPopups
                          id={""}
                          panel_name={"Payee Information"}
                          className="m-b-5"
                          pageName={"costs"}
                          websiteURL={""}
                          dynamic_label={"Payee Name"}
                          name={watch("payee")}
                          address1={watch("address1")}
                          address2={watch("address2")}
                          email={""}
                          city={watch("city")}
                          state={watch("state")}
                          zip_code={watch("zip")}
                          phone_number={""}
                          fax_number={""}
                          buttonData={[]}
                          genrate_doc_address=""
                          specialitie={""}
                          ext={""}
                        />
                      </div>
                      <div className="flex-g-1">
                        <Row className="mx-0">
                          <Col className="ps-0 p-r-5 m-b-5" ref={searchDropdownRef}>
                            <div className="position-relative">
                              <input
                                type="text"
                                placeholder="Search Payee"
                                value={searchTerm}
                                className="form-control custom-margin-bottom p-l-5 p-0 mb-0 rounded-0 height-25"
                                style={{ paddingRight: "24px" }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setIsFiltersOpen(true);
                                  setSearchTerm(value);
                                  debouncedSearch(value);
                                }}
                              />
                              {/* Clear button */}
                              {searchTerm && (
                                <span
                                  onClick={() => {
                                    setSearchTerm("");
                                    setSearchResults([]);
                                    setIsFiltersOpen(false);
                                  }}
                                  style={{
                                    position: "absolute",
                                    right: "10px",
                                    top: "5px",
                                    cursor: "pointer",
                                    color: "var(--primary)",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                    lineHeight: 1,
                                  }}
                                >
                                  ×
                                </span>
                              )}
                            </div>

                            {/* Search results dropdown */}
                            {Array.isArray(searchResults) &&
                              searchResults.length > 0 &&
                              isFiltersOpen && (
                                <div style={{ position: "relative" }}>
                                  <div
                                    style={{
                                      position: "absolute",
                                      zIndex: 1000,
                                      backgroundColor: "#fff",
                                      border: "1px solid #ccc",
                                      width: "100%",
                                      minHeight: "200px",
                                      maxHeight: "200px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {searchResults.map((result, index) => (
                                      <div
                                        key={index}
                                        onClick={() => {
                                          handleResultSelect(result);
                                          setSearchTerm(result?.payee || "");
                                        }}
                                        style={{
                                          height: "25px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #ddd",
                                          gap: "5px",
                                          width: "785px",
                                        }}
                                        className="d-flex align-items-center justify-content-start p-l-5 p-r-5"
                                      >
                                        <img
                                          src={result.page?.icon}
                                          style={{ width: "15px", height: "15px" }}
                                          alt=""
                                        />
                                        <span
                                          style={{
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                          }}
                                        >
                                          {result?.page?.name} {" - "} {result?.panel_bar_name}{" "}
                                          {result?.entity_name} {" - "}{" "}
                                          {result?.payee_contact?.address1}{" "}
                                          {result?.payee_contact?.address2}{" "}
                                          {result?.payee_contact?.city}{" "}
                                          {result?.payee_contact?.state}{" "}
                                          {result?.payee_contact?.zip}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </Col>
                        </Row>

                        {/* Payee name */}
                        <div className="d-flex height-25">
                          <div className="col-12 ps-0 p-r-5 d-flex-1 p-0">
                            <input
                              type="text"
                              className="form-control rounded-0 p-0 p-l-5 height-25"
                              placeholder="Payee"
                              style={formErrors.payee ? { borderColor: "red" } : {}}
                              {...register("payee", {
                                required: "Required",
                                validate: (value) =>
                                  value.trim().length > 0 || "Payee must not be empty",
                              })}
                            />
                          </div>
                        </div>

                        {/* Payee address line 1 + 2 */}
                        <div className="d-flex  mx-0 align-items-center custom-margin-bottom m-t-5">
                          <div className="flex-g-1 ps-0 p-r-5">
                            <input
                              type="text"
                              placeholder="Address 1"
                              className="form-control p-0 p-l-5 rounded-0 height-25"
                              {...register("address1")}
                            />
                          </div>
                          <div className="flex-g-1 p-r-5">
                            <input
                              type="text"
                              placeholder="Address 2"
                              className="form-control p-0 p-l-5 rounded-0 height-25"
                              {...register("address2")}
                            />
                          </div>
                        </div>

                        {/* City / State / Zip */}
                        <div className="row mx-0 align-items-center custom-margin-bottom">
                          <div className="d-flex col-12 ps-0 p-r-5 p-0">
                            {/* City */}
                            <div className="d-flex-1 p-l-0 p-r-5">
                              <input
                                type="text"
                                placeholder="City"
                                className="form-control p-0 p-l-5 rounded-0 height-25"
                                {...register("city")}
                              />
                            </div>

                            {/* State — opens the custom state picker modal */}
                            <div
                              className="d-flex-1 p-l-0 position-relative height-25 p-r-5 custom-select-new-provider"
                              style={{ maxWidth: "90px" }}
                            >
                              <div
                                className="dropdown-button rounded-0 p-0 p-l-5 form-control height-25 d-flex align-items-center"
                                tabIndex={0}
                                onClick={() => setShowStateDropdown((s) => !s)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    setShowStateDropdown((s) => !s);
                                  }
                                }}
                              >
                                <span id="selectedOption">
                                  {payeeState ? (
                                    <div className="d-flex align-items-center">
                                      <svg
                                        style={{
                                          width: "15px",
                                          height: "15px",
                                          fill: "var(--primary-80)",
                                          color: "var(--primary-80)",
                                          stroke: "var(--primary-80)",
                                        }}
                                        className={`icon icon-state-${payeeState}`}
                                      >
                                        <use xlinkHref={`#icon-state-${payeeState}`} />
                                      </svg>
                                      {payeeState}
                                    </div>
                                  ) : (
                                    "State"
                                  )}
                                </span>
                              </div>
                            </div>

                            {/* Zip */}
                            <div className="d-flex-1 pe-0" style={{ maxWidth: "110px" }}>
                              <input
                                type="text"
                                placeholder="Zip"
                                className="form-control rounded-0 p-0 p-l-5 height-25"
                                {...register("zip")}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tab.Pane>

                  {/* ── Payment tab ───────────────────────────────────────── */}
                  <Tab.Pane style={{ minHeight: "355px" }} eventKey="payment">
                    {/* Amount — strips non-numeric chars on change, blocks negative */}
                    <div className="d-flex height-25">
                      <div
                        className="col-2 text-end ps-0 p-r-5"
                        style={{ color: "var(--primary-25)" }}
                      >
                        Amount:
                      </div>
                      <div className="col-10 d-flex-1 p-0 p-r-5 d-flex align-items-center position-relative">
                        <span
                          style={{
                            position: "absolute",
                            left: "8px",
                            lineHeight: "25px",
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        >
                          $
                        </span>
                        <input
                          type="text"
                          placeholder={amountFocused ? "" : "0.00"}
                          className="form-control rounded-0 height-25"
                          style={
                            formErrors.amount
                              ? { borderColor: "red", paddingLeft: "18px" }
                              : { paddingLeft: "18px" }
                          }
                          {...register("amount", {
                            required: "Required",
                            validate: (value) =>
                              parseFloat(value) > 0 && Number.isFinite(parseFloat(value))
                                ? true
                                : "Amount must be positive",
                            onChange: (e) => {
                              const raw = e.target.value.replace(/[^0-9.]/g, "");
                              const parts = raw.split(".");
                              const cleaned =
                                parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : raw;

                              setValue("amount", cleaned, { shouldDirty: true });
                            },
                          })}
                          onFocus={() => {
                            setPreviousAmount(getValues("amount"));
                            setValue("amount", "", { shouldDirty: true });
                            setAmountFocused(true);
                          }}
                          onBlur={() => {
                            setAmountFocused(false);

                            if (!getValues("amount")) {
                              setValue("amount", previousAmount, { shouldDirty: true });
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Check memo */}
                    <div className="d-flex height-25 m-t-5">
                      <div
                        className="col-2 text-end ps-0 p-r-5"
                        style={{ color: "var(--primary-25)" }}
                      >
                        Check Memo:
                      </div>
                      <div className="col-10 d-flex-1 p-0 p-r-5">
                        <input
                          type="text"
                          placeholder="Memo"
                          className="form-control rounded-0 height-25"
                          {...register("memo")}
                        />
                      </div>
                    </div>

                    {/* Invoice number */}
                    <div className="d-flex height-25 m-t-5">
                      <div
                        className="col-2 text-end ps-0 p-r-5"
                        style={{ color: "var(--primary-25)" }}
                      >
                        Invoice Number:
                      </div>
                      <div className="col-10 d-flex-1 p-0 p-r-5">
                        <input
                          type="text"
                          className="form-control rounded-0 height-25"
                          placeholder="Invoice Number"
                          style={formErrors.invoice_number ? { borderColor: "red" } : {}}
                          {...register("invoice_number")}
                        />
                      </div>
                    </div>

                    {/* Category — only shown when adding, not editing */}
                    {/* {!isEdit && ( */}
                    <div className="d-flex height-25 m-t-5">
                      <div
                        className="col-2 text-end ps-0 p-r-5"
                        style={{ color: "var(--primary-25)" }}
                      >
                        Category:
                      </div>
                      <div
                        className="col-10 d-flex-1 p-0 p-r-5 position-relative height-25 custom-select-new-provider"
                        style={{ margin: "0 0 5px 0" }}
                        ref={categoryRef}
                      >
                        <div
                          className="dropdown-button rounded-0 p-0 p-l-5 form-control height-25 d-flex align-items-center"
                          tabIndex={0}
                          onClick={() => setShowPageDropdown((s) => !s)}
                          style={formErrors.category ? { border: "1px solid red" } : {}}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setShowPageDropdown((s) => !s);
                            }
                          }}
                        >
                          <span>
                            {selectedPage ? (
                              <div
                                className="d-flex align-items-center"
                                style={{
                                  gap: "5px",
                                }}
                              >
                                <img
                                  src={selectedPage.page_icon}
                                  style={{ width: "15px", height: "15px" }}
                                  alt=""
                                />
                                {getCategoryLabel(selectedPage)}
                              </div>
                            ) : (
                              "Select Category"
                            )}
                          </span>
                        </div>

                        {showPageDropdown && (
                          <div
                            className="dropdown-menu p-0 mt-0 show"
                            style={{
                              width: "calc(100% - 5px)",
                              border: "1px solid #ced4da",
                              borderRadius: "0px",
                              maxHeight: "200px",
                              overflowY: "auto",
                              scrollbarWidth: "none",
                              zIndex: 1060,
                            }}
                          >
                            {pages?.length > 0 ? (
                              pages.map((page, index) => (
                                <div
                                  key={`${getCategoryPageId(page)}-${getCategoryEntity(page)?.id ?? "page"}-${page?.source_key ?? index}-${index}`}
                                  className="dropdown-option d-flex align-items-center height-25"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handlePageSelect(page)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handlePageSelect(page);
                                    }
                                  }}
                                  style={{
                                    backgroundColor:
                                      index % 2 === 0 ? "var(--primary-5)" : "var(--primary-10)",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                    paddingLeft: "5px",
                                    gap: "5px",
                                  }}
                                >
                                  <img
                                    src={page.page_icon}
                                    style={{ width: "15px", height: "15px" }}
                                    alt=""
                                  />
                                  {getCategoryLabel(page)}
                                </div>
                              ))
                            ) : (
                              <div className="dropdown-option d-flex align-items-center height-25 p-l-5">
                                No pages found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* )} */}

                    {/* Note / free-text */}
                    <div className="d-flex m-t-5">
                      <div
                        className="col-2 text-end ps-0 p-r-5"
                        style={{ color: "var(--primary-25)" }}
                      >
                        Note:
                      </div>
                      <div className="col-10 d-flex-1 p-0 p-r-5">
                        <textarea
                          className="form-control rounded-0"
                          style={{ height: "50px", resize: "none" }}
                          {...register("note")}
                        />
                      </div>
                    </div>

                    {/* Paid By — only shown when adding */}
                    {/* {!isEdit && ( */}
                    <div className="d-flex m-t-5">
                      <div
                        className="col-2 text-end ps-0 p-r-5"
                        style={{ color: "var(--primary-25)" }}
                      >
                        Paid By:
                      </div>

                      <div className="col-10 d-flex-1 p-0 p-r-5">
                        <div className="d-flex align-items-center height-25 m-b-5">
                          <label className="d-flex align-items-center mb-0 col-3 p-l-5 p-r-5">
                            <input
                              type="radio"
                              value="Pending"
                              className="m-r-5 cursor-pointer"
                              {...register("paid_by", {
                                onChange: (e) => {
                                  if (e.target.value === "Pending") setValue("paid_by", "Pending");
                                },
                              })}
                            />
                            Payment Pending
                          </label>
                        </div>
                        {/* Credit card row */}
                        <div className="d-flex align-items-center custom-select-new-provider m-b-5 height-25">
                          <label className="d-flex align-items-center mb-0 col-3 p-l-5 p-r-5">
                            <input
                              type="radio"
                              value="Credit Card"
                              className="m-r-5 cursor-pointer"
                              {...register("paid_by", {
                                onChange: (e) => {
                                  // Selecting the radio clears the check number field
                                  if (e.target.value === "Credit Card")
                                    setValue("check_number", "");
                                },
                              })}
                            />
                            Credit Card
                          </label>

                          {/* Clicking the select also auto-selects the radio */}
                          <select
                            className="form-control rounded-0 height-25 py-0  p-l-5 p-r-5"
                            {...register("credit_card", {
                              onChange: (e) => {
                                if (e.target.value) {
                                  setValue("paid_by", "Credit Card");
                                  setValue("check_number", "");
                                }
                              },
                            })}
                            onClick={() => {
                              if (paidBy !== "Credit Card") {
                                setValue("paid_by", "Credit Card");
                                setValue("check_number", "");
                              }
                            }}
                          >
                            <option value="">
                              {cardsLoading ? "Loading cards..." : "Select Card"}
                            </option>
                            {firmCards?.map((card) => {
                              const value = getCardValue(card);
                              return (
                                <option key={value || getCardLabel(card)} value={value}>
                                  {getCardLabel(card)}
                                </option>
                              );
                            })}
                          </select>
                        </div>

                        {/* Check row */}
                        <div className="d-flex align-items-center height-25">
                          <label className="d-flex align-items-center mb-0 col-3 p-l-5 p-r-5">
                            <input
                              type="radio"
                              value="Check"
                              className="m-r-5 cursor-pointer"
                              {...register("paid_by", {
                                onChange: (e) => {
                                  if (e.target.value === "Check") setValue("credit_card", "");
                                },
                              })}
                            />
                            Check
                          </label>

                          {/* Typing in the check number auto-selects the radio */}
                          <input
                            type="text"
                            placeholder="Check Number"
                            className="form-control rounded-0 height-25  p-l-5 p-r-5"
                            {...register("check_number", {
                              onChange: (e) => {
                                if (e.target.value) {
                                  setValue("paid_by", "Check");
                                  setValue("credit_card", "");
                                }
                              },
                            })}
                            onClick={() => {
                              if (paidBy !== "Check") {
                                setValue("paid_by", "Check");
                                setValue("credit_card", "");
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {/* )} */}

                    {/* Date Due */}
                    <div className="d-flex height-25 m-t-5">
                      <div
                        className="col-2 text-end ps-0 p-r-5"
                        style={{ color: "var(--primary-25)" }}
                      >
                        Date Due:
                      </div>
                      <div className="col-10 d-flex-1 p-r-5 p-0">
                        <input
                          type="date"
                          style={formErrors.date ? { borderColor: "red" } : {}}
                          className="form-control rounded-0 height-25"
                          {...register("date", { required: "Required" })}
                        />
                      </div>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </div>

              {/* Footer actions */}
              <div className="d-flex mt-auto w-100 justify-content-between p-b-5 p-l-5 p-r-5">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  className="height-25"
                  style={{ padding: "0px 12px" }}
                >
                  Cancel
                </Button>

                {isEdit && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={handleDelete}
                    className="height-25"
                    style={{ padding: "0px 12px" }}
                  >
                    Delete
                  </Button>
                )}

                <Button
                  variant="success"
                  type="submit"
                  className="m-l-5 bg-success height-25"
                  style={{ padding: "0px 12px" }}
                >
                  {isEdit ? "Save Cost" : "Add Cost"}
                </Button>
              </div>
            </Tab.Container>
          </form>
        </Modal.Body>
      </Modal>

      {/* State picker — rendered outside the modal to avoid z-index stacking issues */}
      {showStateDropdown && (
        <SelectStateModal
          show={showStateDropdown}
          handleClose={() => setShowStateDropdown(false)}
          onChange={(s) => setValue("state", s?.StateAbr)}
          statesData={statesAbrs}
        />
      )}
    </>
  );
};

export default CostManagementModal;
